import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, School, MapPin, User, XCircle } from "lucide-react";
import { api, formatCOP } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const reference = params.get("reference");
  const transactionId = params.get("transaction_id") || params.get("id") || "";
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!reference) {
      setState("failed");
      return;
    }
    let active = true;

    const poll = async () => {
      try {
        const q = transactionId ? `?transaction_id=${encodeURIComponent(transactionId)}` : "";
        const { data } = await api.get(`/payments/status/${reference}${q}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState("paid");
          clearCart();
          try {
            const o = await api.get(`/orders/by-reference/${reference}`);
            if (active) setOrder(o.data);
          } catch (err) {
            console.error("No se pudo cargar el detalle del pedido:", err);
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (err) {
        console.error("Error verificando el estado del pago:", err);
      }
      attempts.current += 1;
      if (attempts.current > 12) {
        if (active) setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  return (
    <div className="min-h-screen bg-[var(--lc-bg)] lc-dots grid place-items-center px-4 py-16">
      <div className="w-full max-w-lg bg-white rounded-3xl border border-[var(--lc-border)] shadow-xl p-8 sm:p-10 text-center lc-fade-up" data-testid="payment-success-card">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto text-[var(--lc-primary)] animate-spin" />
            <h1 className="mt-5 font-display font-extrabold text-2xl text-[var(--lc-ink)]">
              Confirmando tu pago…
            </h1>
            <p className="text-[var(--lc-slate)] mt-2">Un momento por favor.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto grid place-items-center rounded-full bg-[var(--lc-success)]/15 lc-bounce-in">
              <CheckCircle2 className="w-10 h-10 text-[var(--lc-success)]" />
            </div>
            <h1 className="mt-5 font-display font-black text-3xl text-[var(--lc-ink)]" data-testid="success-title">
              ¡Compra confirmada!
            </h1>
            <p className="text-[var(--lc-slate)] mt-2">
              Enviamos el recibo a tu correo. Coordinaremos la entrega en el colegio.
            </p>

            {order && (
              <div className="mt-6 text-left space-y-4">
                <div className="rounded-2xl border border-[var(--lc-border)] p-4">
                  <p className="text-xs font-bold uppercase text-[var(--lc-slate)] mb-2">
                    Pedido #{order.id.slice(0, 8).toUpperCase()}
                  </p>
                  {order.items.map((it) => (
                    <div key={it.book_id} className="flex justify-between text-sm py-1">
                      <span className="text-[var(--lc-ink)]">
                        {it.title} <span className="text-[var(--lc-slate)]">x{it.quantity}</span>
                      </span>
                      <span className="font-semibold">{formatCOP(it.price * it.quantity)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-[var(--lc-border)] mt-2 pt-2 font-display font-extrabold">
                    <span>Total</span>
                    <span>{formatCOP(order.amount)}</span>
                  </div>
                </div>
                <div className="rounded-2xl bg-[#F0F9FF] p-4 text-sm text-[var(--lc-slate)] space-y-1">
                  {order.delivery?.school ? (
                    <p className="flex items-center gap-2"><School className="w-4 h-4 text-[var(--lc-primary)]" /> {order.delivery.school}</p>
                  ) : order.delivery?.address ? (
                    <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--lc-primary)]" /> {order.delivery.address}</p>
                  ) : null}
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--lc-primary)]" /> {order.delivery?.city}</p>
                  {order.delivery?.student ? (
                    <p className="flex items-center gap-2"><User className="w-4 h-4 text-[var(--lc-primary)]" /> {order.delivery.student}</p>
                  ) : null}
                </div>
              </div>
            )}

            <Link
              to="/"
              data-testid="back-home-button"
              className="mt-7 inline-block rounded-full bg-[var(--lc-primary)] text-white px-8 py-3.5 font-display font-extrabold transition-transform hover:-translate-y-1 shadow-[0_10px_20px_-4px_rgba(14,165,233,0.45)]"
            >
              Volver a la tienda
            </Link>
          </>
        )}

        {state === "failed" && (
          <>
            <div className="w-16 h-16 mx-auto grid place-items-center rounded-full bg-[var(--lc-accent)]/15">
              <XCircle className="w-10 h-10 text-[var(--lc-accent)]" />
            </div>
            <h1 className="mt-5 font-display font-extrabold text-2xl text-[var(--lc-ink)]">
              No pudimos confirmar el pago
            </h1>
            <p className="text-[var(--lc-slate)] mt-2">
              Si el dinero fue debitado, contáctanos. Puedes intentarlo de nuevo.
            </p>
            <Link
              to="/"
              className="mt-7 inline-block rounded-full bg-[var(--lc-ink)] text-white px-8 py-3.5 font-display font-extrabold transition-transform hover:-translate-y-1"
            >
              Volver a la tienda
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
